import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  ColumnLayout,
  Badge,
  StatusIndicator,
  Tabs,
  Flashbar,
  Table,
  Link,
  ProgressBar,
  Alert,
  Spinner,
  Modal,
  Icon,
  Popover,
  ExpandableSection
} from '@cloudscape-design/components';
import { s3Service, getCalculatorDetails, logVisit } from '../services/s3Service';
import PricingCalculatorTab from '../components/PricingCalculatorTab';
import ValidationSummaryBanner from '../components/ValidationSummaryBanner';
import ReviewerFeedbackPanel from '../components/ReviewerFeedbackPanel';
import HistoryTab from '../components/HistoryTab';
import MarkdownRenderer from '../utils/markdownRenderer';
import { useFundRequestId } from '../utils/urlHelper';
import { useHistory } from 'react-router-dom';
import modPathways from '../data/modpathways.json';

const STATUS_COLORS = {
  'RECOMMEND_APPROVAL': 'green',
  'RECOMMEND_REJECTION': 'red',
  'NEEDS_REVIEW': 'blue',
  // Backward compatibility
  'recommend approval': 'green',
  'recommend rejection': 'red',
  'needs review': 'blue',
  'approved': 'green',
  'conditional approval': 'blue',
  'rejected': 'red',
  'pending': 'grey',
  'processing': 'blue',
  'validating': 'blue'
};

const FundingRequestDetail = () => {
  // Get requestId from URL parameters instead of props
  const requestId = useFundRequestId();
  const history = useHistory();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [activeTab, setActiveTab] = useState('approval-summary');
  const [notifications, setNotifications] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [calculatorDetails, setCalculatorDetails] = useState(null);
  const [calculatorLoading, setCalculatorLoading] = useState(false);
  const [showComplianceInfoModal, setShowComplianceInfoModal] = useState(false);
  const [showValidationReportModal, setShowValidationReportModal] = useState(false);
  const [dismissedDuplicatesAlert, setDismissedDuplicatesAlert] = useState(false);

  const addNotification = (type, header, content) => {
    const id = Date.now().toString();
    setNotifications([{
      type,
      header,
      content,
      dismissible: true,
      dismissLabel: "Dismiss",
      onDismiss: () => setNotifications([]),
      id
    }]);
  };

  const loadRequestData = useCallback(async () => {
    // Check if requestId is valid
    if (!requestId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setNotFound(false);
      // Fetch individual request directly from API
      console.log('Loading request data for ID:', requestId);
      const data = await s3Service.getFundingRequestById(requestId);
      console.log('Loaded request data:', data);
      setRequest(data);
      
      // Fetch calculator details for modernization pathways
      try {
        setCalculatorLoading(true);
        console.log('Loading calculator details for ID:', requestId);
        const calculatorData = await getCalculatorDetails(requestId);
        console.log('Loaded calculator details:', calculatorData);
        console.log('Modernization pathways in response:', calculatorData?.modernization_pathways);
        setCalculatorDetails(calculatorData);
      } catch (calcError) {
        console.log('Calculator details not available:', calcError.message);
        setCalculatorDetails(null);
      } finally {
        setCalculatorLoading(false);
      }
    } catch (error) {
      if (error.message.includes('not found') || error.message.includes('404')) {
        setNotFound(true);
      } else {
        // Show error to user
        addNotification('error', 'Failed to Load', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadRequestData();
    if (requestId) logVisit(requestId);
  }, [loadRequestData]);

  const handleValidate = async () => {
    try {
      setValidating(true);
      await s3Service.triggerValidation(requestId);
      
      console.log('Validation triggered, starting status polling...');
      
      // Start polling for status
      let attempts = 0;
      const maxAttempts = 30; // Poll for up to 2.5 minutes
      const pollInterval = 5000; // Poll every 5 seconds
      
      const checkStatus = async () => {
        try {
          const statusData = await s3Service.getReviewStatus(requestId);
          console.log('Status check response:', statusData);
          const reviewState = statusData.review_state || statusData.fundRequests?.[0]?.review_state;
          console.log(`Review state: ${reviewState}, Attempt: ${attempts + 1}/${maxAttempts}`);
          
          attempts++;
          
          if (reviewState === '3-Completed') {
            addNotification('success', 'Validation Completed', 'Validation completed successfully!');
            // Fetch immediately so validation results appear right away
            loadRequestData();
            // Second fetch after delay to pick up agent_narrative, which is written
            // by invoke() after the agent finishes generating the markdown summary
            setTimeout(() => loadRequestData(), 15000);
            setValidating(false);
            return;
          } else if (reviewState === '4-Failed') {
            addNotification('error', 'Validation Failed', 'Validation failed. Please check the details and try again.');
            loadRequestData();
            setValidating(false);
            return;
          } else if (attempts >= maxAttempts) {
            addNotification('warning', 'Status Check Timeout', 'Validation is taking longer than expected. Please refresh manually to see results.');
            setValidating(false);
            return;
          } else {
            // Still in progress, continue polling
            setTimeout(checkStatus, pollInterval);
          }
        } catch (error) {
          console.error('Error checking status:', error);
          if (attempts >= maxAttempts) {
            addNotification('error', 'Status Check Failed', 'Could not verify validation status. Please refresh manually.');
            setValidating(false);
          } else {
            setTimeout(checkStatus, pollInterval);
          }
        }
      };
      
      // Start checking status after a brief delay
      setTimeout(checkStatus, pollInterval);
      
    } catch (error) {
      addNotification('error', 'Validation Failed', error.message);
      setValidating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const parseAmount = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    // Handle "150000.00 USD" format from API
    if (typeof value === 'string') {
      return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
    }
    return 0;
  };

  // Get environment - supports both build-time and runtime config
  const getEnvironment = () => {
    // Try runtime config first (for pipelines)
    if (window._env_?.ENVIRONMENT) {
      return window._env_.ENVIRONMENT;
    }
    
    // Try runtime config (for Pipelines deployment)
    if (window.APP_CONFIG?.ENVIRONMENT) {
      return window.APP_CONFIG.ENVIRONMENT;
    }
    
    // Fallback: detect from URL
    const hostname = window.location.hostname;
    if (hostname.includes('alpha')) return 'alpha';
    if (hostname.includes('gamma')) return 'gamma';
    if (hostname === 'chronos.aws.dev') return 'prod';
    
    return 'unknown';
  };

  const getFundingRequestUrl = (fundRequestId) => {
    const env = getEnvironment();
    const envPrefix = env === 'prod' ? '' : `${env}.`;
    return `https://${envPrefix}funding.apn.a2z.com/aws-approvals/${fundRequestId}/review`;
  };

  const getAceOpportunityUrl = (opportunityId) => {
    // ACE Opportunity ID uses APN Portal Salesforce
    return `https://apn-portal--apnuat2.sandbox.lightning.force.com/${opportunityId}`;
  };

  const getAwsOpportunityUrl = (opportunityId) => {
    // AWS Opportunity ID uses AWS CRM Salesforce
    return `https://aws-crm.lightning.force.com/lightning/r/Opportunity/${opportunityId}/view`;
  };

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <StatusIndicator type="loading">Loading request details...</StatusIndicator>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          <Box variant="h2">Fund Request Not Found</Box>
          <Box variant="p" padding={{ bottom: 'm' }}>
            The fund request ID "{requestId}" could not be found.
          </Box>
          <SpaceBetween direction="horizontal" size="s">
            <Button onClick={() => history.push('/dashboard')}>
              Back to Dashboard
            </Button>

          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  if (!request) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          <Box variant="h2">No Data Available</Box>
          <Box variant="p" padding={{ bottom: 'm' }}>
            Unable to load fund request data.
          </Box>
          <Button onClick={() => history.push('/dashboard')}>
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  const renderOpportunityDetails = () => {
    // Debug: Log the actual request data structure
    console.log('renderOpportunityDetails - Full request object:', request);
    console.log('renderOpportunityDetails - validation_data:', request.validation_data);
    console.log('renderOpportunityDetails - complete_api_data:', request.validation_data?.complete_api_data);
    console.log('renderOpportunityDetails - detailed_validations:', request.validation_data?.detailed_validations);
    
    // Use the ACTUAL DynamoDB structure - data is in fundRequest and opportunity_stage validation
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const project = fundRequest.project || {};
    const partner = fundRequest.partner || {};
    const validationData = request.validation_data?.detailed_validations?.opportunity_stage || {};
    
    // Get opportunity data from opportunities
    const opportunities = fundRequest.opportunities || {};
    const firstOppKey = Object.keys(opportunities)[0];
    const firstOpp = firstOppKey ? opportunities[firstOppKey] : {};
    const oppRecord = firstOpp.record || {};
    
    console.log('renderOpportunityDetails - fundRequest:', fundRequest);
    console.log('renderOpportunityDetails - project:', project);
    console.log('renderOpportunityDetails - oppRecord:', oppRecord);
    console.log('renderOpportunityDetails - validationData:', validationData);
    
    const getValidationStatusType = (status) => {
      if (status === 'validated' || status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'not_enrolled' || status === 'fail') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(validationData.status);

    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Opportunity Details Summary</Header>}>
          <ValidationSummaryBanner 
            label="Opportunity Stage" 
            validationData={validationData} 
          />
        </Container>

        <Container header={<Header variant="h2">Opportunity Information</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Opportunity ID</Box>
              <Link external href={getAceOpportunityUrl(oppRecord.aceOpportunityId || validationData.opportunity_id)}>
                {oppRecord.userFriendlyId || oppRecord.aceOpportunityId || validationData.opportunity_id || '-'}
              </Link>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS opportunity ID</Box>
              <Link external href={getAwsOpportunityUrl(oppRecord.awsOpportunityId || validationData.aws_opportunity_id)}>
                {oppRecord.awsOpportunityId || validationData.aws_opportunity_id || '-'}
              </Link>
            </div>
            <div>
              <Box variant="awsui-key-label">ACE Validation Stage</Box>
              <div>{oppRecord.aceValidationStage || validationData.ace_validation_stage || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Stage</Box>
              <div>{oppRecord.stage || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Partner Development Manager</Box>
              <div>{oppRecord.partnerDevelopmentManager || 'Not Assigned'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Opportunity Owner</Box>
              <div>{oppRecord.ownerName || 'Not Assigned'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS Sales Rep</Box>
              <div>{oppRecord.awsSalesRepName || 'Not Assigned'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Partner Success Manager</Box>
              <div>{oppRecord.partnerSuccessManager || 'Not Assigned'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Creation Date</Box>
              <div>{fundRequest.approvalDetails?.submissionDate ? new Date(fundRequest.approvalDetails.submissionDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Target Close Date</Box>
              <div>{oppRecord.targetCloseDate ? new Date(oppRecord.targetCloseDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Industry Vertical</Box>
              <div>{oppRecord.industryVertical || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer Type</Box>
              <div>{oppRecord.customerType || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Business Unit</Box>
              <div>{oppRecord.customerBizUnit || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">WWPS Partner Development Manager</Box>
              <div>{oppRecord.wwpsPartnerDevelopmentManager || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Territory</Box>
              <div>{oppRecord.territory || project.location?.country || '-'}</div>
            </div>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };

  const renderMPEDetails = () => {
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const mpeValidation = request.validation_data?.detailed_validations?.mpe_program || {};
    
    // Get migration engagement from opportunities
    const opportunities = fundRequest.opportunities || {};
    const firstOppKey = Object.keys(opportunities)[0];
    const firstOpp = firstOppKey ? opportunities[firstOppKey] : {};
    const migrationEngagements = firstOpp.migrationEngagements || [];
    const primaryEngagement = migrationEngagements.find(eng => eng.engagementId === firstOpp.primaryMigrationEngagementId) || migrationEngagements[0] || {};
    
    const getValidationStatusType = (status) => {
      if (status === 'validated' || status === 'qualified' || status === 'qualified_plus' || status === 'pass') return 'success';
      if (status === 'not_qualified' || status === 'fail') return 'error';
      return 'warning';
    };

    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">MPE Details Summary</Header>}>
          <ValidationSummaryBanner 
            label="MPE Program" 
            validationData={mpeValidation} 
          />
        </Container>

        <Container header={<Header variant="h2">MPE Information</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Program Engagement ID</Box>
              <div>{primaryEngagement.engagementId || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">MPE Record Status</Box>
              <div>{primaryEngagement.awsinvRequestStatus || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">MPE Engagement Status</Box>
              <div>{primaryEngagement.status || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Agreement Category</Box>
              <div>{primaryEngagement.migrationEngagementAgreementCategory || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Estimated Migration ARR</Box>
              <div>{(() => {
                if (primaryEngagement.estimatedMigrationArrUsdAmount) {
                  const numericValue = parseFloat(primaryEngagement.estimatedMigrationArrUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return '-';
              })()}</div>
            </div>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h2">MPE Validation</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Validation Status</Box>
              <StatusIndicator type={getValidationStatusType(mpeValidation.status)}>
                {mpeValidation.status?.toUpperCase().replace('_', ' ') || 'UNKNOWN'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Fund Request ARR</Box>
              <div>{mpeValidation.validation_checks?.fund_request_arr ? formatCurrency(mpeValidation.validation_checks.fund_request_arr) : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Estimated Migration ARR</Box>
              <div>{(() => {
                if (primaryEngagement.estimatedMigrationArrUsdAmount) {
                  const numericValue = parseFloat(primaryEngagement.estimatedMigrationArrUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return '-';
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">ARR Valid</Box>
              <StatusIndicator type={mpeValidation.validation_checks?.arr_valid ? 'success' : 'error'}>
                {mpeValidation.validation_checks?.arr_valid ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };





  const renderPartnerEligibility = () => {
    // Debug: Log the actual partner data structure
    console.log('renderPartnerEligibility - partner_eligibility validation:', request.validation_data?.detailed_validations?.partner_eligibility);
    console.log('renderPartnerEligibility - fundRequest partner:', request.validation_data?.complete_api_data?.fundRequest?.partner);
    
    // Use the ACTUAL DynamoDB structure
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const partner = fundRequest.partner || {};
    const owner = fundRequest.owner || {};
    const validationData = request.validation_data?.detailed_validations?.partner_eligibility || {};
    const applications = validationData.applications || [];
    
    const getValidationStatusType = (status) => {
      if (status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'fail') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(validationData.status);

    return (
      <SpaceBetween size="l">
        {/* Partner Eligibility Summary - Custom Display */}
        <Container header={<Header variant="h2">Partner Eligibility Summary</Header>}>
          <SpaceBetween size="s">
            {/* Partner Eligibility */}
            <Box padding={{ vertical: 'xs' }}>
              <Box fontSize="heading-s" fontWeight="bold">
                <StatusIndicator type={validationStatusType}>
                  {validationData.status?.toUpperCase() || 'UNKNOWN'}
                </StatusIndicator>
              </Box>
              {(() => {
                // Show eligibility factors if they exist
                if (validationData.eligibility_factors && validationData.eligibility_factors.length > 0) {
                  return validationData.eligibility_factors.map((factor, index) => {
                    // Fix competency name - replace MCP with MMC
                    let correctedFactor = factor.replace(/^MCP:/g, 'MMC:');
                    correctedFactor = correctedFactor.replace('Migration Competency Program', 'Migration and Modernization Services Competency');
                    return (
                      <Box key={index} fontSize="body-m" color="text-body-secondary" padding={{ top: 'xxs' }}>
                        {correctedFactor}
                      </Box>
                    );
                  });
                }
                // Fallback to reason if no eligibility factors
                return validationData.reason && (
                  <Box fontSize="body-m" color="text-body-secondary" padding={{ top: 'xxs' }}>
                    {validationData.reason}
                  </Box>
                );
              })()}
            </Box>
            
            {/* Specialized Workload - only show if it adds new information */}
            {(() => {
              const specializedData = request.validation_data?.detailed_validations?.specialized_workload;
              const alreadyEligible = validationData.status === 'eligible' || validationData.status === 'pass';
              // If partner is already eligible (MMC/MCR), skip — redundant
              if (alreadyEligible || !specializedData) return null;
              // If partner is not eligible and specialized workload also failed with same verdict, skip — duplicate
              const specResult = specializedData.result || '';
              const partnerResult = validationData.result || '';
              if (specResult === 'fail' && partnerResult === 'fail') return null;
              return (
                <ValidationSummaryBanner 
                  label="Specialized Workload" 
                  validationData={specializedData} 
                />
              );
            })()}
          </SpaceBetween>
        </Container>

        {/* Confirmed Applications */}
        {(() => {
          const confirmedApplications = applications.filter(app => app.status === 'Confirmed');
          
          // Sort applications: those with Covered ARR% > 0 first
          const sortedApplications = [...confirmedApplications].sort((a, b) => {
            const specializedWorkloadData = request.validation_data?.detailed_validations?.specialized_workload;
            const specializedWorkloads = specializedWorkloadData?.specialized_workloads || [];
            
            const aDesignation = a.designationName || a.designation;
            const bDesignation = b.designationName || b.designation;
            
            const aWorkload = specializedWorkloads.find(w => w['Competency or SDP'] === aDesignation);
            const bWorkload = specializedWorkloads.find(w => w['Competency or SDP'] === bDesignation);
            
            const aPercentage = aWorkload ? parseFloat(aWorkload.Percentage) : 0;
            const bPercentage = bWorkload ? parseFloat(bWorkload.Percentage) : 0;
            
            // Sort descending - higher percentages first
            return bPercentage - aPercentage;
          });
          
          return sortedApplications.length > 0 && (
            <Container header={<Header variant="h2" counter={`(${sortedApplications.length})`}>Existing Designations</Header>}>
              <Table
                columnDefinitions={[
                  {
                    id: 'name',
                    header: 'Application ID',
                    cell: item => item.name || '-'
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    cell: item => (
                      <Badge color="green">
                        {item.status || 'Confirmed'}
                      </Badge>
                    )
                  },
                  {
                    id: 'designation',
                    header: 'Designation',
                    cell: item => {
                      return item.designationName || item.designation || '-';
                    }
                  },
                  {
                    id: 'recordType',
                    header: 'Record Type',
                    cell: item => {
                      const recordType = item.recordTypeLabel || item.recordType || 'Service Delivery';
                      return (
                        <Badge color={recordType === 'Competency' ? 'blue' : 'green'}>
                          {recordType}
                        </Badge>
                      );
                    }
                  },
                  {
                    id: 'arrPercentage',
                    header: 'Covered ARR %',
                    cell: item => {
                      // Get specialized workload data from validation
                      const specializedWorkloadData = request.validation_data?.detailed_validations?.specialized_workload;
                      const specializedWorkloads = specializedWorkloadData?.specialized_workloads || [];
                      const unmatchedDesignations = specializedWorkloadData?.unmatched_designations || [];
                      
                      const designation = item.designationName || item.designation;
                      
                      // Check if this designation has specialized workload data
                      const workload = specializedWorkloads.find(w => w['Competency or SDP'] === designation);
                      if (workload) {
                        // Check if this workload meets the threshold
                        const threshold = specializedWorkloadData?.threshold || 75.0;
                        
                        // Backward compatibility: handle both old and new data formats
                        const percentageDisplay = workload.percentage_display || workload.Percentage || '-';
                        const percentageValue = workload.percentage ?? 
                                               (workload.Percentage ? parseFloat(workload.Percentage.replace('%', '')) : 0);
                        const meetsThreshold = percentageValue >= threshold;
                        
                        return (
                          <SpaceBetween direction="horizontal" size="xs">
                            <Box 
                              color={meetsThreshold ? "text-status-success" : "text-status-info"} 
                              fontWeight={meetsThreshold ? "bold" : "normal"}
                            >
                              {percentageDisplay}
                            </Box>
                            {meetsThreshold && (
                              <Badge color="green">Qualifies</Badge>
                            )}
                          </SpaceBetween>
                        );
                      }
                      
                      // Check if this designation is unmatched (not eligible for MAP)
                      const unmatched = unmatchedDesignations.find(u => u.designation === designation);
                      if (unmatched) {
                        return (
                          <StatusIndicator type="warning">
                            Not eligible for MAP
                          </StatusIndicator>
                        );
                      }
                      
                      // No data available
                      return '-';
                    }
                  }
                ]}
                items={sortedApplications}
                loadingText="Loading confirmed applications"
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No confirmed applications</b>
                    <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                      No confirmed partner applications found.
                    </Box>
                  </Box>
                }
              />
            </Container>
          );
        })()}
      </SpaceBetween>
    );
  };

  const renderApplications = () => {
    // Use the ACTUAL DynamoDB structure - applications are in the partner_eligibility validation
    const partnerData = request.validation_data?.detailed_validations?.partner_eligibility || {};
    const partner = request.validation_data?.complete_api_data?.fundRequest?.partner || {};
    
    const applications = partnerData.applications || [];
    
    const getValidationStatusType = (status) => {
      if (status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'fail') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(partnerData.status);

    return (
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Partner Information</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Partner Name</Box>
              <div>{partnerData.partner_name || partner.name || request.requestor || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">SPMS ID</Box>
              <div>{partnerData.spms_id || partner.spmsId || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">MCP Status</Box>
              <StatusIndicator type={partnerData.has_mcp ? 'success' : 'stopped'}>
                {partnerData.has_mcp ? 'Active' : 'Inactive'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">MCR Status</Box>
              <StatusIndicator type={partnerData.has_mcr ? 'success' : 'stopped'}>
                {partnerData.has_mcr ? 'Active' : 'Inactive'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Partner Eligibility Status</Box>
              <StatusIndicator type={validationStatusType}>
                {partnerData.status?.toUpperCase() || 'UNKNOWN'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
        </Container>

        {/* Eligibility Factors */}
        {partnerData.eligibility_factors && partnerData.eligibility_factors.filter(factor => 
          !factor.toLowerCase().includes('strategic collaboration') && 
          !factor.toLowerCase().includes('sca') &&
          !factor.toLowerCase().includes('re:stack')
        ).length > 0 && (
          <Container header={<Header variant="h2">Eligibility Factors</Header>}>
            <Box variant="p">
              <ul style={{ marginTop: '0', paddingLeft: '20px' }}>
                {partnerData.eligibility_factors
                  .filter(factor => 
                    !factor.toLowerCase().includes('strategic collaboration') && 
                    !factor.toLowerCase().includes('sca') &&
                    !factor.toLowerCase().includes('re:stack')
                  )
                  .map((factor, index) => {
                    // Fix competency name - replace MCP with MMC
                    let correctedFactor = factor.replace(/^MCP:/g, 'MMC:');
                    correctedFactor = correctedFactor.replace('Migration Competency Program', 'Migration and Modernization Services Competency');
                    return (
                      <li key={index} style={{ marginBottom: '4px' }}>
                        <Badge color="green">{correctedFactor}</Badge>
                      </li>
                    );
                  })}
              </ul>
            </Box>
          </Container>
        )}

        {/* Applications Table */}
        {applications.length > 0 ? (
          <Container header={<Header variant="h2" counter={`(${applications.length})`}>Confirmed Applications</Header>}>
            <Table
              columnDefinitions={[
                {
                  id: 'name',
                  header: 'Name',
                  cell: item => item.name || '-'
                },
                {
                  id: 'status',
                  header: 'Status',
                  cell: item => {
                    const status = item.status || '-';
                    let color = 'grey';
                    if (status === 'Confirmed') color = 'green';
                    else if (status === 'Submitted') color = 'blue';
                    return (
                      <Badge color={color}>
                        {status}
                      </Badge>
                    );
                  }
                },
                {
                  id: 'awardDate',
                  header: 'Award Date',
                  cell: item => {
                    const date = item.awardDate;
                    if (date) {
                      // Parse date string directly to avoid timezone conversion issues
                      const [year, month, day] = date.split('-');
                      return new Date(year, month - 1, day).toLocaleDateString();
                    }
                    return '-';
                  }
                },
                {
                  id: 'designation',
                  header: 'Designation',
                  cell: item => {
                    // Use the designation from the updated JSON data
                    return item.designationName || item.designation || '-';
                  }
                },
                {
                  id: 'recordType',
                  header: 'Record Type',
                  cell: item => {
                    // Use the recordTypeLabel from the API data
                    const recordType = item.recordTypeLabel || item.recordType || 'Service Delivery';
                    return (
                      <Badge color={recordType === 'Competency' ? 'blue' : 'green'}>
                        {recordType}
                      </Badge>
                    );
                  }
                },
                {
                  id: 'expirationDate',
                  header: 'Expiration Date',
                  cell: item => {
                    const date = item.expirationDate;
                    if (date) {
                      // Parse date string directly to avoid timezone conversion issues
                      const [year, month, day] = date.split('-');
                      return new Date(year, month - 1, day).toLocaleDateString();
                    }
                    return 'No Expiration';
                  }
                },
                {
                  id: 'partnerAccount',
                  header: 'Partner Account',
                  cell: item => item.accountName || item.partnerAccount || '-'
                }
              ]}
              items={applications}
              loadingText="Loading applications"
              empty={
                <Box textAlign="center" color="inherit">
                  <b>No applications</b>
                  <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                    No confirmed applications found.
                  </Box>
                </Box>
              }
            />
          </Container>
        ) : null}
      </SpaceBetween>
    );
  };



  const renderTechnicalRequirements = () => {
    // Get Technical Requirements data from the SOP-organized structure
    const techReqData = request.technicalRequirements || {};
    const migrationDetails = techReqData.migrationDetails || {};
    const validationStatus = techReqData.validationStatus || 'unknown';
    const validationReason = techReqData.validationReason || '';
    const workstreamCompliance = migrationDetails.workstreamCompliance || {};
    
    // Get critical failures from migration details or top-level
    const criticalFailures = migrationDetails.criticalFailures || request.criticalFailures || [];
    
    // Determine validation status indicator type
    const getValidationStatusType = (status) => {
      if (status === 'pass' || status === 'validated') return 'success';
      if (status === 'fail' || status === 'workstream_issues') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(validationStatus);

    return (
      <SpaceBetween size="l">
        {/* Critical Failures Alert - Display prominently at top if present */}
        {criticalFailures.length > 0 && (
          <Container
            header={
              <Header variant="h2">
                <span style={{ color: '#d13212' }}>⚠️ Critical Failures</span>
              </Header>
            }
          >
            <Box variant="p">
              <ul style={{ marginTop: '0', paddingLeft: '20px', color: '#d13212' }}>
                {criticalFailures.map((failure, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                    <Badge color="red">CRITICAL</Badge> {failure}
                  </li>
                ))}
              </ul>
            </Box>
          </Container>
        )}

        {/* Validation Status Indicator */}
        <Container>
          <SpaceBetween size="m">
            <div>
              <Box variant="awsui-key-label">Section Validation Status</Box>
              <StatusIndicator type={validationStatusType}>
                {validationStatus.toUpperCase().replace('_', ' ')}
              </StatusIndicator>
            </div>
            {validationReason && (
              <div>
                <Box variant="awsui-key-label">Validation Details</Box>
                <Box variant="p">{validationReason}</Box>
              </div>
            )}
          </SpaceBetween>
        </Container>

        {/* Migration Details Section */}
        <Container header={<Header variant="h2">Migration Details</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Migration Phase</Box>
              <Badge color={migrationDetails.migrationPhase === 'Mobilize' ? 'blue' : 'green'}>
                {migrationDetails.migrationPhase || '-'}
              </Badge>
            </div>
            <div>
              <Box variant="awsui-key-label">Technical Requirements Status</Box>
              <StatusIndicator type={validationStatusType}>
                {migrationDetails.technicalRequirements || validationStatus.replace('_', ' ').toUpperCase()}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Scope Checklist Present</Box>
              <StatusIndicator type={migrationDetails.scopeChecklistPresent ? 'success' : 'error'}>
                {migrationDetails.scopeChecklistPresent ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
        </Container>

        {/* Translation Notice - Conditional */}
        {(() => {
          const translationNote = migrationDetails.translationNote;
          
          return translationNote && (
            <Alert type="info" header="Translation Notice">
              {translationNote}
            </Alert>
          );
        })()}

        {/* Workstream Compliance Section */}
        {Object.keys(workstreamCompliance).length > 0 && (
          <Container header={<Header variant="h2">Workstream Compliance</Header>}>
            <ColumnLayout columns={3} variant="text-grid">
              {Object.entries(workstreamCompliance).map(([workstream, compliant]) => (
                <div key={workstream}>
                  <Box variant="awsui-key-label">{workstream}</Box>
                  <StatusIndicator type={compliant ? 'success' : 'error'}>
                    {compliant ? 'Compliant' : 'Not Compliant'}
                  </StatusIndicator>
                </div>
              ))}
            </ColumnLayout>
          </Container>
        )}


      </SpaceBetween>
    );
  };

  const renderProjectTimeline = () => {
    // Get Project Timeline data from the SOP-organized structure
    const projectTimelineData = request.projectTimeline || {};
    const timelineDetails = projectTimelineData.timelineDetails || {};
    const validationStatus = projectTimelineData.validationStatus || 'unknown';
    const validationReason = projectTimelineData.validationReason || '';

    // Determine validation status indicator type
    const getValidationStatusType = (status) => {
      if (status === 'pass' || status === 'validated') return 'success';
      if (status === 'fail' || status === 'invalid') return 'error';
      return 'warning';
    };

    const validationStatusType = getValidationStatusType(validationStatus);

    return (
      <SpaceBetween size="l">
        {/* Validation Status Indicator */}
        <Container>
          <SpaceBetween size="m">
            <div>
              <Box variant="awsui-key-label">Section Validation Status</Box>
              <StatusIndicator type={validationStatusType}>
                {validationStatus.toUpperCase()}
              </StatusIndicator>
            </div>
            {validationReason && (
              <div>
                <Box variant="awsui-key-label">Validation Details</Box>
                <Box variant="p">{validationReason}</Box>
              </div>
            )}
          </SpaceBetween>
        </Container>

        {/* Timeline Details Section */}
        <Container header={<Header variant="h2">Timeline Details</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Project Start Date</Box>
              <div>{timelineDetails.projectStartDate || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project End Date</Box>
              <div>{timelineDetails.projectEndDate || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Submitted Date</Box>
              <div>{timelineDetails.submittedDate || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Approved Date</Box>
              <div>{timelineDetails.approvedDate || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS Account ID</Box>
              <div>{timelineDetails.awsAccountId || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project Timeline Status</Box>
              <StatusIndicator type={validationStatusType}>
                {timelineDetails.projectTimeline === 'validated' ? 'Validated' : timelineDetails.projectTimeline === 'invalid' ? 'Invalid' : timelineDetails.projectTimeline || 'Unknown'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
        </Container>


      </SpaceBetween>
    );
  };

  const copyDecisionToClipboard = () => {
    const summary = request.approval_summary || {};
    const decision = summary.decision || 'PENDING';
    const keyFindings = summary.key_findings || [];
    const partner = summary.partner || '';
    const customer = summary.customer || '';
    
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const pricingData = request.validation_data?.detailed_validations?.pricing_calculator || {};
    
    const arr = parseAmount(
      pricingData.annual_recurring_revenue ||
      pricingData.calculator_total_arr ||
      fundRequest.arrUsdAmount ||
      fundRequest.eligibleArrUsdAmount ||
      0
    );
    
    const fundAmount = formatCurrency(arr * 0.2);
    
    const decisionText = `
CHRONOS VALIDATION DECISION

DECISION: ${decision}

KEY FINDINGS:
${keyFindings.map(finding => `• ${finding}`).join('\n')}

Generated by Chronos MAP Validation Platform
Date: ${new Date().toLocaleDateString()}
    `.trim();

    navigator.clipboard.writeText(decisionText).then(() => {
      addNotification('success', 'Decision Copied', 'The validation decision has been copied to your clipboard.');
    }).catch(() => {
      addNotification('error', 'Copy Failed', 'Failed to copy decision to clipboard.');
    });
  };

  const renderValidationSummary = () => {
    const summary = request.approval_summary || {};

    const getDecisionColor = (decision) => {
      const d = decision?.toLowerCase() || '';
      // New categorical decision system
      if (d === 'recommend_approval') return 'green';
      if (d === 'recommend_rejection') return 'red';
      if (d === 'needs_review') return 'blue';
      // Backward compatibility for old records
      if (d.includes('approval')) return 'green';
      if (d.includes('rejection')) return 'red';
      if (d.includes('review')) return 'blue';
      if (d.includes('approve') && !d.includes('reject')) return 'green';
      if (d.includes('reject')) return 'red';
      if (d.includes('conditional')) return 'blue';
      return 'grey';
    };

    return (
      <SpaceBetween size="xs">
        {/* Translation Notice - Conditional */}
        {(() => {
          const attachedFiles = request.validation_data?.detailed_validations?.attached_files || {};
          const scopeChecklist = attachedFiles.validations?.scope_checklist || {};
          const translationNote = scopeChecklist.translation_note;
          
          return translationNote && (
            <Alert type="info" header="Translation Notice">
              {translationNote}
            </Alert>
          );
        })()}
        
        {/* Row 1: Validation Decision */}
        <Container
          header={
            <Header
              actions={
                <Button variant="primary" iconName="copy" onClick={copyDecisionToClipboard}>
                  Copy Decision
                </Button>
              }
            >
              Validation Summary
            </Header>
          }
        >
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Final Decision</Box>
              <Badge color={getDecisionColor(summary.decision)} size="large">
                {summary.decision || 'PENDING'}
              </Badge>
            </div>
            <div>
              <Box variant="awsui-key-label">
                Validations Performed{' '}
                <span 
                  onClick={() => setShowComplianceInfoModal(true)}
                  style={{ cursor: 'pointer', display: 'inline-block' }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && setShowComplianceInfoModal(true)}
                  aria-label="Show validation summary information"
                >
                  <Icon
                    name="status-info"
                    variant="link"
                  />
                </span>
              </Box>
              <Box variant="h3">{summary.validation_summary || summary.compliance_score || '0/0 passed'}</Box>
              <Box variant="small" color="text-body-secondary">
                {(() => {
                  const validationData = request.validation_data?.detailed_validations?.final_decision || {};
                  const categories = validationData.validation_categories || {};
                  // Count validations excluding informational ones (duplicate_funding_requests)
                  const countedValidations = Object.keys(categories).filter(k => k !== 'duplicate_funding_requests').length;
                  // Core validations = 7 (partner_eligibility, mpe_program, opportunity_stage, pricing_calculator, technical_requirements, attached_files, project_timeline)
                  const coreCount = 7;
                  const optionalCount = countedValidations - coreCount;
                  
                  if (optionalCount > 0) {
                    return `Includes ${optionalCount} optional validation(s)`;
                  }
                  return 'Core validations only';
                })()}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">
                Validation Report{' '}
                <span 
                  onClick={() => setShowValidationReportModal(true)}
                  style={{ cursor: 'pointer', display: 'inline-block' }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && setShowValidationReportModal(true)}
                  aria-label="Show validation report"
                >
                  <Icon
                    name="status-info"
                    variant="link"
                  />
                </span>
              </Box>
            </div>
          </ColumnLayout>
        </Container>

        {/* Row 2: Key Findings and Required Actions in Two Columns */}
        {((summary.key_findings && summary.key_findings.length > 0) || (summary.required_actions && summary.required_actions.length > 0)) && (
          <Container>
            <ColumnLayout columns={2} variant="text-grid">
              {/* Key Findings Column */}
              <div>
                <Box variant="awsui-key-label">Key Findings</Box>
                {summary.key_findings && summary.key_findings.length > 0 ? (
                  <Box variant="p">
                    <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                      {summary.key_findings
                        .filter(finding => {
                          // Only show cost variance finding on rejections
                          const isCostVarianceFinding = finding.toLowerCase().includes('cost variance') || finding.toLowerCase().includes('total project cost');
                          if (isCostVarianceFinding) {
                            const decision = (summary.decision || '').toLowerCase();
                            return decision === 'recommend_rejection' || decision.includes('reject');
                          }
                          return true;
                        })
                        .map((finding, index) => {
                        // Add visual indicators if not already present
                        let displayFinding = finding;
                        if (!finding.match(/^[✅❌⚠️]/)) {
                          // Determine appropriate icon based on content
                          if (finding.toLowerCase().includes('recommend approval') || 
                              finding.toLowerCase().includes('approved') || 
                              finding.toLowerCase().includes('pass') || 
                              finding.toLowerCase().includes('compliant') ||
                              finding.toLowerCase().includes('validated') ||
                              finding.toLowerCase().includes('confirmed') ||
                              finding.toLowerCase().includes('eligible')) {
                            displayFinding = `✅ ${finding}`;
                          } else if (finding.toLowerCase().includes('recommend rejection') ||
                                   finding.toLowerCase().includes('rejected') || 
                                   finding.toLowerCase().includes('fail') || 
                                   finding.toLowerCase().includes('missing') ||
                                   finding.toLowerCase().includes('not found') ||
                                   finding.toLowerCase().includes('exceeded')) {
                            displayFinding = `❌ ${finding}`;
                          } else if (finding.toLowerCase().includes('invalid_account_id_warning') ||
                                   finding.toLowerCase().includes('warning')) {
                            displayFinding = `⚠️ ${finding}`;
                          } else {
                            displayFinding = `⚠️ ${finding}`;
                          }
                        }
                        
                        return (
                          <li key={index} style={{ marginBottom: '4px' }}>
                            {displayFinding}
                          </li>
                        );
                      })}
                    </ul>
                  </Box>
                ) : (
                  <Box variant="p" color="text-body-secondary">No key findings available</Box>
                )}
              </div>

              {/* Required Actions Column */}
              <div>
                <Box variant="awsui-key-label">Required Actions</Box>
                {summary.required_actions && summary.required_actions.length > 0 ? (
                  <Box variant="p">
                    <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                      {summary.required_actions.map((action, index) => {
                        // Account ID warning on Assess phase — show as ⚠️ note, not ACTION REQUIRED
                        const isAccountIdWarning = action.toLowerCase().includes('account id') &&
                          action.toLowerCase().includes('flagged for review');
                        return (
                          <li key={index} style={{ marginBottom: '4px' }}>
                            {isAccountIdWarning
                              ? <span>⚠️ {action}</span>
                              : <><Badge color="blue">ACTION REQUIRED</Badge> {action}</>
                            }
                          </li>
                        );
                      })}
                    </ul>
                  </Box>
                ) : (
                  <Box variant="p" color="text-body-secondary">No actions required</Box>
                )}
              </div>
            </ColumnLayout>
          </Container>
        )}
      </SpaceBetween>
    );
  };

  const renderMigrationDetails = () => {
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const techData = request.validation_data?.detailed_validations?.technical_requirements || {};
    const pricingData = request.validation_data?.detailed_validations?.pricing_calculator || {};
    const incentives = fundRequest.incentives || {};
    
    const getValidationStatusType = (status) => {
      if (status === 'validated' || status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'fail') return 'error';
      return 'warning';
    };

    return (
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Migration Details</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Migration Phase</Box>
              <Badge color="blue">
                {fundRequest.migrationPhase || 'Mobilize'}
              </Badge>
            </div>
            <div>
              <Box variant="awsui-key-label">Annual Recurring Revenue (ARR) (USD)</Box>
              <div>{(() => {
                if (fundRequest.arrUsdAmount) {
                  const numericValue = parseFloat(fundRequest.arrUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return pricingData.annual_recurring_revenue ? formatCurrency(pricingData.annual_recurring_revenue) : '-';
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Eligible ARR (USD)</Box>
              <div>{(() => {
                if (fundRequest.eligibleArrUsdAmount) {
                  const numericValue = parseFloat(fundRequest.eligibleArrUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return pricingData.calculator_total_arr ? formatCurrency(pricingData.calculator_total_arr) : '-';
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Modernization ARR (USD)</Box>
              <div>{(() => {
                if (fundRequest.modernizationArrUsdAmount) {
                  const numericValue = parseFloat(fundRequest.modernizationArrUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return '-';
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Modernization Incentive (USD)</Box>
              <div>{(() => {
                if (fundRequest.modernizationIncentiveUsdAmount) {
                  const numericValue = parseFloat(fundRequest.modernizationIncentiveUsdAmount.replace(/[^0-9.-]+/g, ''));
                  return formatCurrency(numericValue);
                }
                return '-';
              })()}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">VMware Acknowledgement</Box>
              <StatusIndicator type={incentives.vmware?.eligible ? 'success' : 'stopped'}>
                {incentives.vmware?.eligible ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Funding Type</Box>
              <Badge color="blue">{fundRequest.fundingType || '-'}</Badge>
            </div>
            <div>
              <Box variant="awsui-key-label">Public Sector</Box>
              <StatusIndicator type={fundRequest.isPublicSector ? 'success' : 'stopped'}>
                {fundRequest.isPublicSector ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Greenfield Eligible</Box>
              <StatusIndicator type={incentives.greenfield?.eligible ? 'success' : 'stopped'}>
                {incentives.greenfield?.eligible ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">VMware Incentive Eligible</Box>
              <StatusIndicator type={incentives.vmware?.eligible ? 'success' : 'stopped'}>
                {incentives.vmware?.eligible ? 'Yes' : 'No'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    );
  };

  const renderProjectDetails = () => {
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const project = fundRequest.project || {};
    const approvalDetails = fundRequest.approvalDetails || {};
    const timelineData = request.validation_data?.detailed_validations?.project_timeline || {};
    
    const getValidationStatusType = (status) => {
      if (status === 'validated' || status === 'eligible' || status === 'pass') return 'success';
      if (status === 'not_eligible' || status === 'fail') return 'error';
      return 'warning';
    };

    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Project Details Summary</Header>}>
          <ValidationSummaryBanner 
            label="Project Timeline" 
            validationData={timelineData} 
          />
        </Container>

        <Container header={<Header variant="h2">Project Information</Header>}>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Activity Name</Box>
              <div>{project.activityName || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Budget Year</Box>
              <div>{project.budgetYear || request.budget_year || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer Considering</Box>
              <div>{project.customerConsidering || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer RFx</Box>
              <div>{project.customerRfx || 'None'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Third Party Funding</Box>
              <div>{project.thirdPartyFunding || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project Country</Box>
              <div>{project.location?.country || '-'} {project.location?.states?.join(', ') || ''}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project Location GEO</Box>
              <div>{project.location?.locationGeo || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Funding Expiration Date</Box>
              <div>{project.expirationDate ? new Date(project.expirationDate).toLocaleDateString() : '-'}</div>
            </div>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h2">Project Timeline</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Project Start Date</Box>
              <div>{project.startDate ? new Date(project.startDate).toLocaleDateString() : (timelineData.project_start_date ? new Date(timelineData.project_start_date).toLocaleDateString() : '-')}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project End Date</Box>
              <div>{project.endDate ? new Date(project.endDate).toLocaleDateString() : (timelineData.project_end_date ? new Date(timelineData.project_end_date).toLocaleDateString() : '-')}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Submission Date</Box>
              <div>{approvalDetails.submissionDate ? new Date(approvalDetails.submissionDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Approval Date</Box>
              <div>{approvalDetails.approvalDate ? new Date(approvalDetails.approvalDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Last Stage Transition</Box>
              <div>{fundRequest.lastStageTransitionDate ? new Date(fundRequest.lastStageTransitionDate).toLocaleDateString() : '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">AWS Account ID</Box>
              <div>{project.awsAccountId || timelineData.aws_account_id || '-'}</div>
              {(() => {
                const accountId = project.awsAccountId || timelineData.aws_account_id || '';
                if (/^0+$/.test(accountId)) {
                  return (
                    <Alert type="warning" statusIconAriaLabel="Warning" style={{ marginTop: '8px' }}>
                      Warning: The AWS Account ID provided ({accountId}) is not valid. While this requires attention before project completion, no immediate action is needed at the Assess phase.
                    </Alert>
                  );
                }
                return null;
              })()}
            </div>
            <div>
              <Box variant="awsui-key-label">Timeline Status</Box>
              <StatusIndicator type={getValidationStatusType(timelineData.status)}>
                {timelineData.status?.toUpperCase() || 'UNKNOWN'}
              </StatusIndicator>
            </div>
          </ColumnLayout>
          {timelineData.status === 'duration_exceeded' && (
            <Box padding={{ top: 'm' }}>
              <Alert type="warning" header="Project Duration Exceeds Threshold">
                Project duration of {timelineData.validation_checks?.duration_months} months exceeds
                the {timelineData.validation_checks?.map_program?.replace('_', ' ')} limit
                of {timelineData.validation_checks?.max_duration_months} months. This fund request
                requires further review.
              </Alert>
            </Box>
          )}
        </Container>
      </SpaceBetween>
    );
  };



  const getModernizationPathway = (serviceCode, serviceName) => {
    const pathway = modPathways.find(p => 
      p.service_code === serviceCode || 
      p.service_name === serviceName
    );
    return pathway ? pathway.Modernization_Pathway : 'Non Modern';
  };

  const renderTechnicalReview = () => {
    // Real API data from validation results and calculator details
    const validationData = request.validation_data?.detailed_validations || {};
    const techReq = validationData.technical_requirements || {};
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    
    // Get real data from chronos_calc_review_history via calculator details API
    const calculatorData = calculatorDetails || {};
    const servicesData = calculatorData.services || [];
    
    // Calculate modernization metrics using the same logic as PricingCalculatorTab
    const totalServices = servicesData.length;
    const modernServices = servicesData.filter(service => 
      getModernizationPathway(service.service_code, service.service_name) !== 'Non Modern'
    );
    const modernizationIndex = totalServices > 0 ? Math.round((modernServices.length / totalServices) * 100) : 0;
    
    // Calculate total modern ARR from services using the same pathway logic
    const totalModernArr = modernServices.reduce((sum, service) => sum + (service.monthly_cost * 12 || 0), 0);
    
    // Calculate EC2 contribution for containerization potential
    const ec2Services = servicesData.filter(service => 
      service.service_name && service.service_name.toLowerCase().includes('ec2')
    );
    const ec2Contribution = ec2Services.reduce((sum, service) => sum + (service.monthly_cost * 12 * 0.3 || 0), 0);
    
    // Group services by modernization pathway using the same logic as PricingCalculatorTab
    const pathwayGroups = {};
    servicesData.forEach(service => {
      const pathway = getModernizationPathway(service.service_code, service.service_name);
      if (!pathwayGroups[pathway]) {
        pathwayGroups[pathway] = { count: 0, arr: 0 };
      }
      pathwayGroups[pathway].count += 1;
      pathwayGroups[pathway].arr += (service.monthly_cost * 12 || 0);
    });
    
    // Get the top modernization pathways (excluding Non Modern) - AFTER pathwayGroups is created
    const modernPathways = Object.entries(pathwayGroups)
      .filter(([pathway]) => pathway !== 'Non Modern')
      .sort(([,a], [,b]) => b.arr - a.arr);
    
    const technicalData = {
      // Real modernization metrics from calculator data
      modernizationIndex: modernizationIndex,
      totalModernArr: totalModernArr,
      pathwayGroups: pathwayGroups,
      modernPathways: modernPathways,
      ec2Contribution: ec2Contribution,
      totalServices: totalServices,
      modernServices: modernServices.length,
      
      // Real technical requirements data
      migrationPhase: techReq.migration_phase || fundRequest.migrationPhase || 'Unknown',
      validationStatus: techReq.status || 'unknown',
      validationReason: techReq.reason || 'No validation data available',
      complianceScore: techReq.result || techReq.compliance_score || 'unknown',
      
      // Real workstream compliance data
      workstreamCompliance: techReq.validation_checks?.workstream_compliance || {},
      allWorkstreamsCompliant: techReq.validation_checks?.all_workstreams_compliant || false,
      scopeChecklistPresent: techReq.scope_checklist_present || false,
      
      // VMware acknowledgement from fund request
      vmwareAcknowledgement: fundRequest.accurateWorkloadAcknowledgement || false,
      
      // Data availability flags
      hasCalculatorData: calculatorData && Object.keys(calculatorData).length > 0,
      calculatorLoading: calculatorLoading
    };

    return (
      <SpaceBetween size="l">
        {/* Translation Notice - Conditional */}
        {(() => {
          const attachedFiles = request.validation_data?.detailed_validations?.attached_files || {};
          const scopeChecklist = attachedFiles.validations?.scope_checklist || {};
          const translationNote = scopeChecklist.translation_note;
          
          return translationNote && (
            <Alert type="info" header="Translation Notice">
              {translationNote}
            </Alert>
          );
        })()}
        
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Technical Review Summary</Header>}>
          <SpaceBetween size="s">
            <ValidationSummaryBanner 
              label="Technical Requirements" 
              validationData={techReq} 
            />
            {(() => {
              const attachedFilesData = request.validation_data?.detailed_validations?.attached_files;
              return attachedFilesData && (
                <ValidationSummaryBanner 
                  label="Attached Files" 
                  validationData={attachedFilesData} 
                />
              );
            })()}
          </SpaceBetween>
        </Container>

        {/* Supporting Documents - Moved from Supporting Documents tab */}
        {(() => {
          const attachedFilesData = request.validation_data?.detailed_validations?.attached_files || {};
          const validations = attachedFilesData.validations || {};
          
          const getValidationStatusType = (status) => {
            if (status === 'validated' || status === 'present' || status === 'pass') return 'success';
            if (status === 'not_eligible' || status === 'fail' || status === 'missing') return 'error';
            if (status === 'not_required') return 'info';
            return 'warning';
          };

          // Create documents list from validations
          const documentsList = Object.entries(validations)
            .map(([key, validation]) => ({
              id: key,
              name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              type: validation.file_name ? 'File' : 'Validation',
              fileName: validation.file_name || '-',
              documentId: validation.document_id || '-',
              status: validation.status || 'unknown',
              parsed: validation.parsed || false,
              rowsParsed: validation.rows_parsed || 0,
              applicable: validation.applicable !== false
            }))
            .sort((a, b) => {
              // Scope Checklist always first
              if (a.id === 'scope_checklist') return -1;
              if (b.id === 'scope_checklist') return 1;
              // Rest alphabetically by name
              return a.name.localeCompare(b.name);
            });

          return (
            <SpaceBetween size="l">
              {/* Document Summaries - Only show documents with status: "present" */}
              {(() => {
                const presentDocs = Object.entries(validations)
                  .filter(([key, validation]) => validation.status === 'present' && validation.summary)
                  .map(([key, validation]) => ({ key, ...validation }));

                if (presentDocs.length === 0) return null;

                return presentDocs.map(doc => {
                  const summary = doc.summary || {};
                  const docName = doc.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  
                  const getStatusColor = (status) => {
                    if (status === 'compliant') return 'success';
                    if (status === 'issues_found') return 'warning';
                    return 'info';
                  };

                  return (
                    <Container 
                      key={doc.key}
                      header={
                        <Header variant="h2">
                          {docName} Summary
                        </Header>
                      }
                    >
                      <SpaceBetween size="m">
                        {/* Status and Key Metrics */}
                        <ColumnLayout columns={4} variant="text-grid">
                          <div>
                            <Box variant="awsui-key-label">Status</Box>
                            <StatusIndicator type={getStatusColor(summary.status)}>
                              {summary.status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
                            </StatusIndicator>
                          </div>
                          {summary.phase && (
                            <div>
                              <Box variant="awsui-key-label">Phase</Box>
                              <Badge color="blue">{summary.phase}</Badge>
                            </div>
                          )}
                          {summary.compliance_summary && (
                            <div>
                              <Box variant="awsui-key-label">Compliance</Box>
                              <div>{summary.compliance_summary}</div>
                            </div>
                          )}
                          {summary.total_servers !== undefined && (
                            <div>
                              <Box variant="awsui-key-label">Total Servers</Box>
                              <div>{summary.total_servers}</div>
                            </div>
                          )}
                          {summary.critical_issues !== undefined && (
                            <div>
                              <Box variant="awsui-key-label">Critical Issues</Box>
                              <Badge color={summary.critical_issues > 0 ? 'red' : 'green'}>
                                {summary.critical_issues}
                              </Badge>
                            </div>
                          )}
                          {summary.cost_optimization_potential && (
                            <div>
                              <Box variant="awsui-key-label">Cost Optimization Potential</Box>
                              <div>{summary.cost_optimization_potential}</div>
                            </div>
                          )}
                        </ColumnLayout>

                        {/* Key Findings */}
                        {summary.key_findings && summary.key_findings.length > 0 && (
                          <div>
                            <Box variant="awsui-key-label">Key Findings</Box>
                            <Box variant="p">
                              <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                                {summary.key_findings.map((finding, index) => {
                                  // Remove emoji characters from findings
                                  const cleanFinding = finding.replace(/[\u2705\u274c\u26a0\ufe0f\u2139]/g, '').trim();
                                  return (
                                    <li key={index} style={{ marginBottom: '4px' }}>
                                      {cleanFinding}
                                    </li>
                                  );
                                })}
                              </ul>
                            </Box>
                          </div>
                        )}

                        {/* Cost Comparison — always shown when check was applicable */}
                        {(() => {
                          const costCmp = techReq.cost_comparison || {};
                          if (!costCmp.check_applicable) return null;
                          const exceeded = costCmp.variance_usd > costCmp.threshold_usd;
                          return (
                            <div>
                              <Box variant="awsui-key-label">Total Project Cost Comparison</Box>
                              <ColumnLayout columns={2} variant="text-grid">
                                <div>
                                  <Box variant="awsui-key-label">APFP ({costCmp.local_currency})</Box>
                                  <div>
                                    {costCmp.apfp_total_project_cost_local?.toLocaleString()} {costCmp.local_currency}
                                    {costCmp.apfp_total_project_cost_usd != null && (
                                      <Box variant="small" color="text-body-secondary"> (≈${costCmp.apfp_total_project_cost_usd?.toLocaleString()} USD)</Box>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Scope Checklist</Box>
                                  <div>
                                    {costCmp.scope_checklist_cost_raw || '-'}
                                    {costCmp.scope_checklist_cost_usd != null && (
                                      <Box variant="small" color="text-body-secondary"> (≈${costCmp.scope_checklist_cost_usd?.toLocaleString()} USD)</Box>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Variance (USD)</Box>
                                  <StatusIndicator type={exceeded ? 'error' : 'success'}>
                                    ${costCmp.variance_usd?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    {exceeded ? ` — exceeds $${costCmp.threshold_usd?.toLocaleString()} threshold` : ' — within threshold'}
                                  </StatusIndicator>
                                </div>
                              </ColumnLayout>
                            </div>
                          );
                        })()}

                        {/* Recommendations */}
                        {summary.recommendations && summary.recommendations.length > 0 && (
                          <div>
                            <Box variant="awsui-key-label">Recommendations</Box>
                            <Box variant="p">
                              <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                                {summary.recommendations.map((rec, index) => (
                                  <li key={index} style={{ marginBottom: '4px' }}>
                                    <Badge color="blue">ACTION</Badge> {rec}
                                  </li>
                                ))}
                              </ul>
                            </Box>
                          </div>
                        )}

                        {/* Issues */}
                        {summary.issues && summary.issues.length > 0 && (
                          <Alert type="warning" header="Issues Found">
                            <ul style={{ marginTop: '0', paddingLeft: '20px', marginBottom: '0' }}>
                              {summary.issues.map((issue, index) => (
                                <li key={index} style={{ marginBottom: '4px' }}>
                                  {issue}
                                </li>
                              ))}
                            </ul>
                          </Alert>
                        )}

                        {/* File Name */}
                        <div>
                          <Box variant="awsui-key-label">File Name</Box>
                          <div>{doc.file_name || '-'}</div>
                        </div>
                      </SpaceBetween>
                    </Container>
                  );
                });
              })()}

              <Container header={<Header variant="h2">Document Validations</Header>}>
                <Table
                  columnDefinitions={[
                    {
                      id: 'name',
                      header: 'Document Type',
                      cell: item => item.name
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: item => (
                        <StatusIndicator type={getValidationStatusType(item.status)}>
                          {item.status?.toUpperCase() || 'UNKNOWN'}
                        </StatusIndicator>
                      )
                    },
                    {
                      id: 'fileName',
                      header: 'File Name',
                      cell: item => item.fileName || '-'
                    },
                    {
                      id: 'parsed',
                      header: 'Parsed',
                      cell: item => {
                        // Show "-" if document is not applicable
                        if (!item.applicable) {
                          return '-';
                        }
                        return (
                          <StatusIndicator type={item.parsed ? 'success' : 'error'}>
                            {item.parsed ? 'Yes' : 'No'}
                          </StatusIndicator>
                        );
                      }
                    },
                    {
                      id: 'applicable',
                      header: 'Applicable',
                      cell: item => (
                        <StatusIndicator type={item.applicable ? 'success' : 'info'}>
                          {item.applicable ? 'Yes' : 'No'}
                        </StatusIndicator>
                      )
                    }
                  ]}
                  items={documentsList}
                  loadingText="Loading document validations"
                  empty={
                    <Box textAlign="center" color="inherit">
                      <b>No document validations found</b>
                      <Box variant="p" color="inherit">
                        No document validation data is available for this fund request.
                      </Box>
                    </Box>
                  }
                />
              </Container>

              {validations.scope_checklist && validations.scope_checklist.status === 'present' && (
                <Container header={<Header variant="h2">Scope Checklist Details</Header>}>
                  <SpaceBetween size="m">
                  <Alert type="info">
                    The scope checklist information below is displayed for reference purposes only and does not influence any validation or approval outcomes.
                  </Alert>
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">File Name</Box>
                      <div>{validations.scope_checklist.file_name || '-'}</div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">All Workstreams Compliant</Box>
                      {(() => {
                        // scope_checklist is the source of truth for compliance
                        const allCompliant = validations.scope_checklist.all_workstreams_compliant;
                        return (
                          <StatusIndicator type={allCompliant ? 'success' : 'error'}>
                            {allCompliant ? 'Yes' : 'No'}
                          </StatusIndicator>
                        );
                      })()}
                    </div>
                  </ColumnLayout>
                  
                  {(() => {
                    // Use scope_checklist as source of truth for workstream compliance
                    const workstreamCompliance = validations.scope_checklist.workstream_compliance || {};
                    return Object.keys(workstreamCompliance).length > 0 && (
                      <Box padding={{ top: 'm' }}>
                        <Box variant="awsui-key-label">Workstream Compliance</Box>
                        <Table
                          columnDefinitions={[
                            {
                              id: 'workstream',
                              header: 'Workstream',
                              cell: item => item.workstream
                            },
                            {
                              id: 'compliant',
                              header: 'Compliant',
                              cell: item => (
                                <StatusIndicator type={item.compliant ? 'success' : 'error'}>
                                  {item.compliant ? 'Yes' : 'No'}
                                </StatusIndicator>
                              )
                            }
                          ]}
                          items={Object.entries(workstreamCompliance).map(([workstream, compliant]) => ({
                            workstream,
                            compliant: compliant === true
                          }))}
                          variant="embedded"
                        />
                      </Box>
                    );
                  })()}

                  {/* Notes for Approvers - from assess and mobilize phases */}
                  {(() => {
                    const phases = validations.scope_checklist.phases || {};
                    const assessNotes = phases.assess?.notes_for_approvers;
                    const mobilizeNotes = phases.mobilize?.notes_for_approvers;
                    const hasNotes = assessNotes || mobilizeNotes;
                    if (!hasNotes) return null;
                    return (
                      <Box padding={{ top: 'm' }}>
                        <SpaceBetween size="s">
                          {assessNotes && (
                            <div>
                              <Box variant="awsui-key-label">Notes for Approvers (Assess)</Box>
                              <Box variant="p">{assessNotes}</Box>
                            </div>
                          )}
                          {mobilizeNotes && (
                            <div>
                              <Box variant="awsui-key-label">Notes for Approvers (Mobilize)</Box>
                              <Box variant="p">{mobilizeNotes}</Box>
                            </div>
                          )}
                        </SpaceBetween>
                      </Box>
                    );
                  })()}
                  </SpaceBetween>
                </Container>
              )}

              {validations.genai_bedrock && validations.genai_bedrock.applicable && (
                <Container header={<Header variant="h2">GenAI Bedrock Validation</Header>}>
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Status</Box>
                      <StatusIndicator type={getValidationStatusType(validations.genai_bedrock.status)}>
                        {validations.genai_bedrock.status?.toUpperCase() || 'UNKNOWN'}
                      </StatusIndicator>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Applicable</Box>
                      <StatusIndicator type={validations.genai_bedrock.applicable ? 'success' : 'info'}>
                        {validations.genai_bedrock.applicable ? 'Yes' : 'No'}
                      </StatusIndicator>
                    </div>
                  </ColumnLayout>
                </Container>
              )}

              {validations.connect_spreadsheet && validations.connect_spreadsheet.applicable && (
                <Container header={<Header variant="h2">Connect Spreadsheet Validation</Header>}>
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Status</Box>
                      <StatusIndicator type={getValidationStatusType(validations.connect_spreadsheet.status)}>
                        {validations.connect_spreadsheet.status?.toUpperCase() || 'UNKNOWN'}
                      </StatusIndicator>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Applicable</Box>
                      <StatusIndicator type={validations.connect_spreadsheet.applicable ? 'success' : 'info'}>
                        {validations.connect_spreadsheet.applicable ? 'Yes' : 'No'}
                      </StatusIndicator>
                    </div>
                  </ColumnLayout>
                </Container>
              )}
            </SpaceBetween>
          );
        })()}




      </SpaceBetween>
    );
  };

  const renderClaims = () => {
    const claimsData = request.validation_data?.detailed_validations?.attached_files?.validations?.customer_sign_off || {};
    const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
    const migrationPhase = fundRequest.migrationPhase || '';
    const stage = fundRequest.stage || '';
    
    const getStatusType = (status) => {
      if (status === 'present') return 'success';
      if (status === 'not_found') return 'error';
      return 'warning';
    };

    // Handle not found case
    if (claimsData.status === 'not_found') {
      return (
        <Container>
          <Alert type="warning" header="Customer Sign-Off Document Not Found">
            {claimsData.reason || 'Customer sign-off document not found or download failed'}
          </Alert>
        </Container>
      );
    }

    // Handle parsing failed case
    if (claimsData.status === 'present' && !claimsData.parsed) {
      return (
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Document Information</Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">File Name</Box>
                <div>{claimsData.file_name || '-'}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Status</Box>
                <StatusIndicator type="error">Parsing Failed</StatusIndicator>
              </div>
            </ColumnLayout>
          </Container>
          <Alert type="error" header="Document Parsing Failed">
            {claimsData.parse_error || 'Failed to parse customer sign-off document'}
          </Alert>
        </SpaceBetween>
      );
    }

    // Handle successful parsing
    const rawData = claimsData.data || {};
    const data = rawData.customer_sign_off_form_data || rawData;
    const crossValidation = rawData.cst_validation_with_scope_checklist || null;
    const showAssess = stage === 'cash_claim' && migrationPhase === 'Assess';
    const showMobilize = stage === 'cash_claim' && migrationPhase === 'Mobilize';
    
    const claimsRec = claimsData.claims_recommendation;

    const getDecisionColor = (d) => {
      if (d === 'RECOMMEND_APPROVAL') return 'green';
      if (d === 'RECOMMEND_REJECTION') return 'red';
      return 'blue';
    };
    
    return (
      <SpaceBetween size="l">
        {/* Scope Checklist Warning */}
        {claimsData.scope_checklist_warning && (
          <Alert type="warning" header="Scope Checklist Issue">
            {claimsData.scope_checklist_warning}
          </Alert>
        )}

        {/* CST Format Warning */}
        {claimsData.cst_format_warning && (
          <Alert type="warning" header="Document Format Issue">
            {claimsData.cst_format_warning}
          </Alert>
        )}

        {/* Claims Approver Recommendation */}
        {claimsRec && (
          <Container header={<Header variant="h2">Claims Recommendation</Header>}>
            <SpaceBetween size="m">
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Decision</Box>
                  <Badge color={getDecisionColor(claimsRec.decision)}>
                    {claimsRec.decision?.replace(/_/g, ' ') || 'PENDING'}
                  </Badge>
                </div>
                <div>
                  <Box variant="awsui-key-label">Reason</Box>
                  <div>{claimsRec.reason || '-'}</div>
                </div>
              </ColumnLayout>
              {claimsRec.key_findings?.length > 0 && (
                <div>
                  <Box variant="awsui-key-label">Key Findings</Box>
                  <ul style={{ marginTop: '4px', paddingLeft: '20px', marginBottom: '0' }}>
                    {claimsRec.key_findings.map((f, i) => (
                      <li key={i} style={{ marginBottom: '4px' }}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
              {claimsRec.required_actions?.length > 0 && (
                <div>
                  <Box variant="awsui-key-label">Required Actions</Box>
                  <ul style={{ marginTop: '4px', paddingLeft: '20px', marginBottom: '0' }}>
                    {claimsRec.required_actions.map((a, i) => (
                      <li key={i} style={{ marginBottom: '4px' }}>
                        <Badge color="blue">ACTION REQUIRED</Badge> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </SpaceBetween>
          </Container>
        )}

        <Container header={<Header variant="h2">Claim Information</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">File Name</Box>
              <div>{claimsData.file_name || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Project Name</Box>
              <div>{data.project_name || '-'}</div>
            </div>
            {fundRequest.isPublicSector && (
            <div>
              <Box variant="awsui-key-label">Public Sector</Box>
              <Badge color="blue">YES</Badge>
            </div>
            )}
            <div>
              <Box variant="awsui-key-label">Claim Fund Request ID</Box>
              <div>{data.claim_fund_request_id || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">Start Date</Box>
              <div>{data.start_date || '-'}</div>
            </div>
            <div>
              <Box variant="awsui-key-label">End Date</Box>
              <div>{data.end_date || '-'}</div>
            </div>
          </ColumnLayout>
        </Container>

        {/* Assess Phase Workstreams - Only show for Assess phase */}
        {showAssess && data.assess_phase_workstreams && (
          <Container header={<Header variant="h2">Assess Phase Workstreams</Header>}>
            <Table
              columnDefinitions={[
                {
                  id: 'workstream',
                  header: 'Workstream',
                  cell: item => item.workstream
                },
                {
                  id: 'completed',
                  header: 'Completed',
                  cell: item => (
                    <StatusIndicator type={item.completed === 'YES' ? 'success' : 'stopped'}>
                      {item.completed || '-'}
                    </StatusIndicator>
                  )
                }
              ]}
              items={[
                { workstream: 'Initial Business Case', completed: data.assess_phase_workstreams.initial_business_case },
                { workstream: 'Initial Discovery', completed: data.assess_phase_workstreams.initial_discovery },
                { workstream: 'Strategy Analysis (7Rs)', completed: data.assess_phase_workstreams.strategy_analysis_7rs }
              ]}
              variant="embedded"
            />
          </Container>
        )}

        {/* Mobilize Phase Workstreams - Only show for Mobilize phase */}
        {showMobilize && data.mobilize_phase_workstreams && (
          <Container header={<Header variant="h2">Mobilize Phase Workstreams</Header>}>
            <Table
              columnDefinitions={[
                {
                  id: 'workstream',
                  header: 'Workstream',
                  cell: item => item.workstream
                },
                {
                  id: 'completed',
                  header: 'Completed',
                  cell: item => (
                    <StatusIndicator type={item.completed === 'YES' ? 'success' : 'stopped'}>
                      {item.completed || '-'}
                    </StatusIndicator>
                  )
                }
              ]}
              items={[
                { workstream: 'Detailed Business Case', completed: data.mobilize_phase_workstreams.detailed_business_case },
                { workstream: 'Detailed Discovery', completed: data.mobilize_phase_workstreams.detailed_discovery },
                { workstream: 'Planning and Governance', completed: data.mobilize_phase_workstreams.planning_and_governance },
                { workstream: 'Landing Zone', completed: data.mobilize_phase_workstreams.landing_zone },
                { workstream: 'Security and Compliance', completed: data.mobilize_phase_workstreams.security_and_compliance },
                { workstream: 'Operating Model', completed: data.mobilize_phase_workstreams.operating_model },
                { workstream: 'People & Culture', completed: data.mobilize_phase_workstreams.people_and_culture || data.mobilize_phase_workstreams.people_skills_culture_change_leadership },
                { workstream: 'Migration and Modernization Experience', completed: data.mobilize_phase_workstreams.migration_and_modernization_experience }
              ]}
              variant="embedded"
            />
          </Container>
        )}

        {/* Acknowledgments and Confirmations */}
        <Container header={<Header variant="h2">Acknowledgments and Confirmations</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Partner Acknowledgment Completed</Box>
              <StatusIndicator type={data.partner_acknowledgment_completed === 'YES' ? 'success' : 'stopped'}>
                {data.partner_acknowledgment_completed || '-'}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer Authorization Signed</Box>
              <StatusIndicator type={data.customer_authorization_signed?.includes('YES') ? 'success' : 'stopped'}>
                {data.customer_authorization_signed || '-'}
              </StatusIndicator>
            </div>
            {migrationPhase === 'Mobilize' && (
            <div>
              <Box variant="awsui-key-label">Migration to AWS Completion Checked</Box>
              <StatusIndicator type={data.migration_to_aws_completion_checked === 'YES' ? 'success' : 'stopped'}>
                {data.migration_to_aws_completion_checked || '-'}
              </StatusIndicator>
            </div>
            )}
            {migrationPhase === 'Mobilize' && (
            <div>
              <Box variant="awsui-key-label">Modernization Pathways Confirmation Checked</Box>
              <StatusIndicator type={data.modernization_pathways_confirmation_checked === 'YES' ? 'success' : 'stopped'}>
                {data.modernization_pathways_confirmation_checked || '-'}
              </StatusIndicator>
            </div>
            )}
          </ColumnLayout>
        </Container>

        {/* Cross-Validation: CST vs Scope Checklist */}
        {crossValidation && (
          <Container header={<Header variant="h2">CST vs Scope Checklist Cross-Validation</Header>}>
            {crossValidation.scope_checklist_validation_passed ? (
              <StatusIndicator type="success">
                All {crossValidation.matched_count} workstreams match between CST and scope checklist
              </StatusIndicator>
            ) : (
              <SpaceBetween size="m">
                <Alert type="warning" header={`${crossValidation.mismatch_count} mismatch(es) found between CST and scope checklist`}>
                  The following workstreams have different values in the Customer Sign-Off form compared to the scope checklist.
                </Alert>
                <Table
                  columnDefinitions={[
                    { id: 'workstream', header: 'Workstream', cell: item => item.workstream },
                    { id: 'scopeChecklist', header: 'Scope Checklist', cell: item => (
                      <Badge color={item.scope_checklist === 'yes' ? 'green' : 'grey'}>
                        {item.scope_checklist === 'yes' ? 'In Scope' : 'Not In Scope'}
                      </Badge>
                    )},
                    { id: 'signOffForm', header: 'CST Sign-Off', cell: item => (
                      <Badge color={item.sign_off_form === 'YES' ? 'green' : 'grey'}>
                        {item.sign_off_form}
                      </Badge>
                    )},
                  ]}
                  items={crossValidation.mismatches || []}
                  variant="embedded"
                />
              </SpaceBetween>
            )}
          </Container>
        )}
      </SpaceBetween>
    );
  };

  const renderPricingCalculator = () => {
    const pricingData = request.validation_data?.detailed_validations?.pricing_calculator || {};
    const modernizationData = request.validation_data?.detailed_validations?.modernization_pathways;
    
    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Pricing Calculator Summary</Header>}>
          <SpaceBetween size="s">
            <ValidationSummaryBanner 
              label="Pricing Calculator" 
              validationData={pricingData} 
            />
            {modernizationData && modernizationData.status !== 'not_applicable' && (
              <ValidationSummaryBanner 
                label="Modernization Pathways" 
                validationData={modernizationData} 
              />
            )}
          </SpaceBetween>
        </Container>
        
        <PricingCalculatorTab request={request} />
      </SpaceBetween>
    );
  };

  const getTabIcon = (validationStatus) => {
    const status = validationStatus?.toLowerCase();
    if (status === 'validated' || status === 'eligible' || status === 'approved' || status === 'qualified' || status === 'qualified_plus' || status === 'pass' || status === 'recommend_approval' || status === 'recommend approval') {
      return '✅ ';
    } else if (status === 'rejected' || status === 'not_eligible' || status === 'not_qualified' || status === 'fail' || status === 'failed' || status === 'recommend_rejection' || status === 'recommend rejection') {
      return '❌ ';
    } else if (status === 'warning' || status === 'conditional' || status === 'pending' || status === 'needs_review' || status === 'needs review' || status === 'needs_attention') {
      return '⚠️ ';
    }
    return '⚠️ '; // Default to warning if status is unknown
  };

  const getPricingCalculatorStatus = () => {
    // Use the agent's validation status which includes all checks:
    // - ARR variance (Request vs Calculator)
    // - Optimization threshold (Not Optimized ARR ≤ 10%)
    // - Excluded services (Data Transfer, Support, etc.)
    // - URL validation
    const pricingValidation = validationData.pricing_calculator || {};
    const agentStatus = pricingValidation.status;
    
    // If agent validation exists, use it
    if (agentStatus) {
      // Map agent statuses to tab icon statuses
      if (agentStatus === 'validated') return 'validated';
      if (agentStatus === 'failed_optimization' || 
          agentStatus === 'arr_variance_exceeded' || 
          agentStatus === 'critical_excluded_services' ||
          agentStatus === 'invalid_url') return 'fail';
      return agentStatus; // pending, not_run, etc.
    }
    
    // Fallback: If no agent validation, return pending
    return 'pending';
  };

  const renderHistory = () => {
    return <HistoryTab fundRequestId={requestId} request={request} />;
  };

  const renderDuplicatesTab = () => {
    const duplicateData = request.validation_data?.detailed_validations?.duplicate_funding_requests || {};
    const duplicates = duplicateData.duplicates || [];
    const hasDuplicates = duplicates.length > 0;

    if (!hasDuplicates) {
      return (
        <Container>
          <Box textAlign="center" padding="xxl">
            <StatusIndicator type="success">
              <Box variant="h3">No Duplicates Found</Box>
              <Box variant="p" color="inherit" padding={{ top: 's' }}>
                This funding request has no duplicate entries in the system.
              </Box>
            </StatusIndicator>
          </Box>
        </Container>
      );
    }

    return (
      <SpaceBetween size="l">
        {/* Validation Decision */}
        <Container header={<Header variant="h2">Duplicates Summary</Header>}>
          <ValidationSummaryBanner 
            label="Duplicate Funding Requests" 
            validationData={duplicateData} 
          />
        </Container>

        <Alert type="warning" header={duplicateData.reason || `Found ${duplicates.length} duplicate FR(s)`}>
          The following funding requests may be duplicates based on matching criteria.
        </Alert>

        <Container>
          <Table
            columnDefinitions={[
              {
                id: 'fundRequestId',
                header: 'Potential Duplicate Fund Request Id',
                cell: item => (
                  <Link href={`/fund-requests/${item.fundRequestId}/details`}>
                    {item.fundRequestId}
                  </Link>
                ),
                sortingField: 'fundRequestId',
                width: 280
              },
              {
                id: 'opportunityId',
                header: 'Ace Opportunity Id',
                cell: item => item.opportunityId || '-',
                sortingField: 'opportunityId',
                width: 120
              },
              {
                id: 'programEngagementId',
                header: 'Program Engagement Id',
                cell: item => item.programEngagementId || '-',
                sortingField: 'programEngagementId',
                width: 150
              },
              {
                id: 'migrationPhase',
                header: 'Migration Phase',
                cell: item => item.migrationPhase || '-',
                sortingField: 'migrationPhase',
                width: 120
              },
              {
                id: 'activityName',
                header: 'Activity Name',
                cell: item => item.activityName || '-',
                sortingField: 'activityName',
                width: 200
              },
              {
                id: 'partnerName',
                header: 'Partner Name',
                cell: item => item.partnerName || '-',
                sortingField: 'partnerName',
                width: 150
              },
              {
                id: 'approvedAmount',
                header: 'Approved Amount',
                cell: item => item.approvedAmount ? formatCurrency(parseFloat(item.approvedAmount)) : '-',
                sortingField: 'approvedAmount',
                width: 130
              },
              {
                id: 'stage',
                header: 'Stage',
                cell: item => item.stage || '-',
                sortingField: 'stage',
                width: 120
              },
              {
                id: 'awsOpportunityId',
                header: 'AWS Opportunity Id',
                cell: item => item.awsOpportunityId || '-',
                sortingField: 'awsOpportunityId',
                width: 150
              },
              {
                id: 'businessDescription',
                header: 'Business Description',
                cell: item => (
                  <Box>
                    {item.businessDescription ? (
                      <Box variant="small">{item.businessDescription.substring(0, 100)}{item.businessDescription.length > 100 ? '...' : ''}</Box>
                    ) : '-'}
                  </Box>
                ),
                width: 250
              },
              {
                id: 'customerCompanyName',
                header: 'Ace Opportunity Customer Company Name',
                cell: item => item.customerCompanyName || '-',
                sortingField: 'customerCompanyName',
                width: 200
              }
            ]}
            items={duplicates}
            loadingText="Loading duplicates"
            sortingDisabled={false}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No duplicates found</b>
              </Box>
            }
            header={
              <Header
                counter={`(${duplicates.length})`}
                description="Funding requests that may be duplicates based on matching identifiers"
              >
                Fund Request Duplicates
              </Header>
            }
          />
        </Container>
      </SpaceBetween>
    );
  };

  const validationData = request.validation_data?.detailed_validations || {};
  
  const tabs = [
    {
      id: 'approval-summary',
      label: `${getTabIcon(request.approval_summary?.decision)}Approval Summary`,
      content: renderValidationSummary()
    },
    {
      id: 'migration-details',
      label: 'Migration Details',
      content: renderMigrationDetails()
    },
    {
      id: 'partner-eligibility',
      label: `${getTabIcon(validationData.partner_eligibility?.result)}Partner Eligibility`,
      content: renderPartnerEligibility()
    },
    {
      id: 'opportunity-details',
      label: `${getTabIcon(validationData.opportunity_stage?.result)}Opportunity Details`,
      content: renderOpportunityDetails()
    },
    // Conditionally show MPE Details tab only for MAP_LARGE
    ...(request.validation_data?.detailed_validations?.final_decision?.map_program === 'MAP_LARGE' ? [{
      id: 'mpe-details',
      label: `${getTabIcon(validationData.mpe_program?.result)}MPE Details`,
      content: renderMPEDetails()
    }] : []),
    {
      id: 'project-details',
      label: `${getTabIcon(validationData.project_timeline?.status === 'invalid_account_id_warning' ? 'warning' : validationData.project_timeline?.result)}Project Details`,
      content: renderProjectDetails()
    },
    {
      id: 'pricing-calculator',
      label: `${getTabIcon(getPricingCalculatorStatus())}Pricing Calculator`,
      content: renderPricingCalculator()
    },
    {
      id: 'technical-review',
      label: `${getTabIcon(validationData.technical_requirements?.result)}Technical Review`,
      content: renderTechnicalReview()
    },
    {
      id: 'history',
      label: 'History',
      content: renderHistory()
    },
    // Conditionally show Claims tab only when customer sign-off is parsed
    ...(validationData.attached_files?.validations?.customer_sign_off?.parsed === true ? [{
      id: 'claims',
      label: `${getTabIcon(validationData.attached_files?.validations?.customer_sign_off?.claims_recommendation?.decision)}Claims`,
      content: renderClaims()
    }] : []),
    // Conditionally show Duplicates tab only when duplicates exist
    ...(() => {
      const duplicateData = request.validation_data?.detailed_validations?.duplicate_funding_requests || {};
      const duplicates = duplicateData.duplicates || [];
      const count = duplicates.length;
      
      return count > 0 ? [{
        id: 'duplicates',
        label: `⚠️ Duplicates (${count})`,
        content: renderDuplicatesTab()
      }] : [];
    })()
  ];

  return (
    <SpaceBetween size="s">
      <Flashbar items={notifications} />
      
      {/* Duplicates Warning Banner */}
      {(() => {
        const duplicateData = request.validation_data?.detailed_validations?.duplicate_funding_requests || {};
        const duplicates = duplicateData.duplicates || [];
        if (duplicates.length > 0 && !dismissedDuplicatesAlert) {
          return (
            <Alert
              type="warning"
              dismissible
              onDismiss={() => setDismissedDuplicatesAlert(true)}
              header="Possible Duplicates Detected"
            >
              We identified possible duplicates with this fund request. Refer to the <Link onFollow={() => setActiveTab('duplicates')}>Fund Request Duplicates Table</Link> for additional details.
            </Alert>
          );
        }
        return null;
      })()}
      
      {/* Validation Summary Info Modal */}
      <Modal
        visible={showComplianceInfoModal}
        onDismiss={() => setShowComplianceInfoModal(false)}
        header="Validation Summary Explanation"
        size="large"
        footer={
          <Box float="right">
            <Button variant="primary" onClick={() => setShowComplianceInfoModal(false)}>
              Close
            </Button>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">
            <Box variant="strong">The validation summary shows how many SOP checks passed, failed, or need attention for a MAP funding request.</Box>
          </Box>
          
          <Box variant="h3">Validations Performed</Box>
          <Box variant="p" color="text-body-secondary">
            The system performs up to 10 validations. Some validations are optional and only run when applicable to the fund request.
          </Box>
          <SpaceBetween size="s">
            {(() => {
              const keyFindings = request.approval_summary?.key_findings || [];
              
              // Helper function to find matching key finding for a validation
              const findKeyFinding = (keywords) => {
                return keyFindings.find(finding => 
                  keywords.some(keyword => finding.toLowerCase().includes(keyword.toLowerCase()))
                );
              };
              
              const validations = [
                { name: '1. Partner Eligibility (MMC, MCR status)', keywords: ['partner', 'eligibility'] },
                { name: '2. Specialized Workload (optional - only for specific competencies)', keywords: ['specialized workload', 'specialized_workload'] },
                { name: '3. MPE Program Validation (enrollment and ARR match)', keywords: ['mpe program', 'mpe_program'] },
                { name: '4. Opportunity Stage Alignment', keywords: ['opportunity stage', 'opportunity_stage'] },
                { name: '5. Duplicate Funding Requests (informational only, not counted in total)', keywords: ['duplicate'] },
                { name: '6. Pricing Calculator (URL, ARR variance, optimization, excluded services)', keywords: ['calculator', 'pricing'] },
                { name: '7. Technical Requirements (workstreams, scope checklist)', keywords: ['technical requirements', 'technical_requirements'] },
                { name: '8. Attached Files (required documents)', keywords: ['attached files', 'attached_files'] },
                { name: '9. Project Timeline (AWS Account ID, dates)', keywords: ['timeline', 'project_timeline'] },
                { name: '10. Modernization Pathways (optional - only for modernization incentives)', keywords: ['modernization'] }
              ];
              
              return validations.map((validation, index) => {
                const keyFinding = findKeyFinding(validation.keywords);
                return (
                  <Box key={index}>
                    <Box>{validation.name}</Box>
                    {keyFinding && (
                      <Box variant="small" color="text-body-secondary" padding={{ left: 'l', top: 'xxs' }}>
                        {keyFinding}
                      </Box>
                    )}
                  </Box>
                );
              });
            })()}
          </SpaceBetween>
          
          <Alert type="info" header="Recommendation">
            <SpaceBetween size="xs">
              <Box>
                <Box variant="strong">Decision:</Box> {request.approval_summary?.decision || '-'}
              </Box>
              <Box>
                <Box variant="strong">Recommendation:</Box> {request.approval_summary?.recommendation || '-'}
              </Box>
              <Box>
                <Box variant="strong">Validation Summary:</Box> {request.approval_summary?.validation_summary || '-'}
              </Box>
            </SpaceBetween>
          </Alert>

          {request.approval_summary?.notes_for_approvers && (
            <Alert type="warning" header="Note for Approvers">
              {request.approval_summary.notes_for_approvers}
            </Alert>
          )}

          <Box variant="h3">How It Works</Box>
          <Box variant="p">
            Each validation is categorized as one of three results:
          </Box>
          
          <SpaceBetween size="s">
            <Box>
              <Box variant="strong">✅ Pass</Box> — Validation fully meets SOP requirements
            </Box>
            <Box>
              <Box variant="strong">⚠️ Needs Attention</Box> — Partially meets requirements, human review recommended
            </Box>
            <Box>
              <Box variant="strong">❌ Fail</Box> — Does not meet SOP requirements
            </Box>
          </SpaceBetween>

          <Box variant="h3">Decision Outcomes</Box>
          <SpaceBetween size="s">
            <Box>
              <Badge color="green">RECOMMEND APPROVAL</Badge> — All validations pass, no critical findings
            </Box>
            <Box>
              <Badge color="red">RECOMMEND REJECTION</Badge> — Critical findings detected (see below)
            </Box>
            <Box>
              <Badge color="yellow">NEEDS REVIEW</Badge> — No critical findings, but some validations failed or need attention. Human review required.
            </Box>
          </SpaceBetween>
          
        </SpaceBetween>
      </Modal>

      {/* Validation Report Modal */}
      <Modal
        visible={showValidationReportModal}
        onDismiss={() => setShowValidationReportModal(false)}
        header="Validation Report"
        size="large"
        footer={
          <Box float="right">
            <Button variant="primary" onClick={() => setShowValidationReportModal(false)}>
              Close
            </Button>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="info">
            This is the narrative generated by Chronos as it validated this fund request. It reflects the agent's reasoning and findings at the time of processing.
          </Alert>
          
          {(() => {
            // Agent narrative: top-level field (new records) or fallback to nested path (existing records)
            let agentNarrative = request.agent_narrative
              || request.validation_data?.detailed_validations?.final_decision?.validation_report?._final_summary
              || '';
            
            // Handle raw Strands response objects: {'role':'assistant','content':[{'text':'...'}]}
            if (agentNarrative && typeof agentNarrative === 'object') {
              const c = agentNarrative.content || agentNarrative;
              agentNarrative = Array.isArray(c) && c[0]?.text ? c[0].text : JSON.stringify(c);
            }
            // Handle stringified Python dicts (existing records saved before fix)
            // Can't use JSON.parse — content contains apostrophes that break quote replacement.
            // Extract text by slicing off the known wrapper structure.
            if (typeof agentNarrative === 'string' && agentNarrative.startsWith("{'role'")) {
              const marker = "'text': '";
              const startIdx = agentNarrative.indexOf(marker);
              if (startIdx !== -1) {
                // Text starts after marker, ends before '}]}' (or trailing variants)
                let extracted = agentNarrative.slice(startIdx + marker.length);
                // Remove trailing wrapper: '}]}" or '}]}' or similar
                extracted = extracted.replace(/'\}[\]\}]*\s*$/, '');
                agentNarrative = extracted.replace(/\\n/g, '\n').replace(/\\'/g, "'");
              }
            }
            
            if (!agentNarrative) {
              return (
                <Box textAlign="center" padding="l">
                  <Box variant="p" color="text-body-secondary">
                    No validation reports available for this fund request.
                  </Box>
                </Box>
              );
            }
            
            return (
              <>
                {/* Agent narrative — the full markdown summary */}
                {agentNarrative && (
                  <Container header={<Header variant="h3">Agent Summary</Header>}>
                    <MarkdownRenderer content={agentNarrative} />
                  </Container>
                )}
                

              </>
            );
          })()}
        </SpaceBetween>
      </Modal>

      {/* Header Section */}
      <Container
        header={
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => history.push('/dashboard')}>Back to List</Button>
                <Button 
                  variant="primary" 
                  onClick={handleValidate}
                  loading={validating}
                  disabled={validating}
                >
                  {validating ? 'Validating...' : 'Validate'}
                </Button>
              </SpaceBetween>
            }
          >
            {request.apfp_fundrequestid || request.id}
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/* Processed Date - Prominent Display */}
          {request.processed_date && (
            <Box textAlign="right">
              <Box variant="small" color="text-body-secondary">
                Last Processed: {new Date(request.processed_date).toLocaleString()}
              </Box>
            </Box>
          )}
          
          <div>
            <Badge color={STATUS_COLORS[request.status] || STATUS_COLORS[request.status?.toUpperCase()?.replace(/ /g, '_')] || 'grey'}>
              {request.status?.toUpperCase()?.replace(/ /g, '_') || request.status}
            </Badge>
          </div>
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Request ID</Box>
              <Link
                external
                href={getFundingRequestUrl(request.apfp_fundrequestid || request.id)}
              >
                {request.apfp_fundrequestid || request.id}
              </Link>
              <Box variant="awsui-key-label" padding={{ top: 's' }}>Benefit Application ID</Box>
              <div>{request.validation_data?.complete_api_data?.fundRequest?.benefitApplicationId || '-'}</div>
              <Box variant="awsui-key-label" padding={{ top: 's' }}>Annual Run Rate (ARR)</Box>
              <Box fontSize="heading-s" fontWeight="bold" color="text-status-success">
                {(() => {
                  const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                  const pricingData = request.validation_data?.detailed_validations?.pricing_calculator || {};
                  
                  const arr = parseAmount(
                    pricingData.annual_recurring_revenue ||
                    pricingData.calculator_total_arr ||
                    fundRequest.arrUsdAmount ||
                    fundRequest.eligibleArrUsdAmount ||
                    0
                  );
                  
                  return formatCurrency(arr);
                })()}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Partner</Box>
              <div>{request.validation_data?.complete_api_data?.fundRequest?.partner?.name || request.partner || '-'}</div>
              <Box variant="awsui-key-label" padding={{ top: 's' }}>SPMS ID</Box>
              <div>{(() => {
                const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                const partner = fundRequest.partner || {};
                const owner = fundRequest.owner || {};
                const validationData = request.validation_data?.detailed_validations?.partner_eligibility || {};
                return validationData.spms_id || partner.spmsId || owner.partnerSpmsId || 'Not Available';
              })()}</div>
              <Box variant="awsui-key-label" padding={{ top: 's' }}>
                {(() => {
                  const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                  const currency = fundRequest.fundingDetails?.totalFundingRequestedFromAwsLocalCurrency || 'USD';
                  return `Total Funds Requested from AWS`;
                })()}
              </Box>
              <Box fontSize="heading-s" fontWeight="bold">
                {(() => {
                  const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                  const amountStr = fundRequest.fundingDetails?.totalFundingRequestedFromAwsLocalAmount || '-';
                  
                  if (amountStr === '-') return '-';
                  
                  // Parse amount and currency from string like "9122300000.00 IDR"
                  const parts = amountStr.split(' ');
                  const amount = parseFloat(parts[0]);
                  const currency = parts[1] || 'USD';
                  
                  // Format with commas
                  const formatted = new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                  }).format(amount);
                  
                  return `${currency} ${formatted}`;
                })()}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Customer</Box>
              <div>{request.approval_summary?.customer || request.customer || '-'}</div>
              <Box variant="awsui-key-label" padding={{ top: 's' }}>Migration Phase</Box>
              <Badge color="blue">
                {request.validation_data?.complete_api_data?.fundRequest?.migrationPhase || 
                 request.validation_data?.detailed_validations?.technical_requirements?.migration_phase || 
                 'Mobilize'}
              </Badge>
              <Box variant="awsui-key-label" padding={{ top: 's' }}>Program Type</Box>
              <Badge color="blue">
                {(() => {
                  // Determine MAP program based on ARR amount
                  const fundRequest = request.validation_data?.complete_api_data?.fundRequest || {};
                  const arr = parseAmount(
                    fundRequest.arrUsdAmount ||
                    fundRequest.eligibleArrUsdAmount ||
                    0
                  );
                  return arr > 500000 ? 'MAP LARGE' : 'MAP LITE';
                })()}
              </Badge>
              {request.validation_data?.complete_api_data?.fundRequest?.isPublicSector && (
                <Box padding={{ top: 'xs' }}>
                  <Badge color="blue">PUBLIC SECTOR</Badge>
                </Box>
              )}
            </div>
          </ColumnLayout>
          
          <ColumnLayout columns={1} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Project Description</Box>
              <div>{request.validation_data?.complete_api_data?.fundRequest?.project?.description || request.description || 'Migration and Modernization Project'}</div>
            </div>
          </ColumnLayout>
        </SpaceBetween>
      </Container>

      {/* Reviewer Feedback Panel - only show when validation data exists */}
      {request.validation_data && (
        <ReviewerFeedbackPanel
          fundRequestId={requestId}
          validationDecision={request.approval_status || request.approval_summary?.decision}
          validationChecks={Object.keys(request.validation_data?.detailed_validations || {})}
        />
      )}

      {/* Horizontal Tabs */}
      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId)}
        tabs={tabs}
      />
    </SpaceBetween>
  );
};

export default FundingRequestDetail;
